import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { SELECTION_COLUMN_DEF } from '../lib/gridUtils';
import { Search, Plus, ExternalLink, CheckCircle, X } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import ArbiSelect from '../components/ui/ArbiSelect';
import ExportButton from '../components/ui/ExportButton';
import { triggerTicker } from '../lib/activityTicker';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { Toast, type ToastState } from '../components/ui/Toast';
import { createNotification } from '../lib/notifications';
import { useApp } from '../hooks/useApp';
import { extractEnvelope, extractBulkEnvelope } from '../lib/extractEnvelope';
import { resolveAssigneeLabel } from '../lib/taskAssignee';
import type { PostgrestError } from '@supabase/supabase-js';

ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Fan out task_assigned (to new assignee) + task_unassigned (to previous
 * assignee) for every task whose assigned_to changed between pre- and
 * post-RPC state. Caller passes the post-state rows (RPC return) and a
 * pre-state map keyed by task id. Both events fire fire-and-forget through
 * createNotification.
 */
function fireAssigneeTransitions(
  postRows: Array<{ id: string; title: string | null; assigned_to: string | null }>,
  preAssignees: Map<string, string | null>
): void {
  for (const row of postRows) {
    const before = preAssignees.get(row.id) ?? null;
    const after = row.assigned_to ?? null;
    if (before === after) continue;
    if (before) {
      createNotification({
        userId: before,
        event: 'task_unassigned',
        title: `Task unassigned: ${row.title ?? 'Untitled'}`,
        message: 'A task previously assigned to you has been reassigned.',
        link: `/tasks?id=${row.id}`,
      });
    }
    if (after) {
      createNotification({
        userId: after,
        event: 'task_assigned',
        title: `Task assigned: ${row.title ?? 'Untitled'}`,
        message: 'A task has been assigned to you.',
        link: `/tasks?id=${row.id}`,
      });
    }
  }
}

// ── Local types ───────────────────────────────────────────────────────────────

type TaskStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';
type TaskPriority = 'critical' | 'high' | 'medium' | 'low';
type TaskType =
  | 'po_approval_needed'
  | 'receiving_ready'
  | 'discrepancy_review'
  | 'flag_review'
  | 'file_prep'
  | 'manual';

interface TaskRow {
  id: string;
  title: string | null;
  description: string | null;
  task_type: TaskType | string;
  priority: TaskPriority | string;
  status: TaskStatus | string;
  assigned_to: string | null;
  due_date: string | null;
  deep_link_path: string | null;
  created_at: string | null;
  total_count?: number | string | null;
}

interface ActionConfigEntry {
  label: string;
  fallback: string | null;
}

// Day 84.6 P4: error type widened to PostgrestError | null to compose with
// extractEnvelope / extractBulkEnvelope Path 1 translation.
type GenericSupabaseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// Narrow Task shape for fireAssigneeTransitions consumer (Day 84.6 P4.5).
type TaskNotifyShape = { id: string; title: string | null; assigned_to: string | null };

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_FILTERS: ReadonlyArray<{ id: TaskStatus | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
] as const;

const PRIORITY_FILTERS: ReadonlyArray<{ id: TaskPriority | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
] as const;

const TYPE_FILTERS: ReadonlyArray<{ id: TaskType | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'po_approval_needed', label: 'PO Approval' },
  { id: 'receiving_ready', label: 'Receiving' },
  { id: 'discrepancy_review', label: 'Discrepancy' },
  { id: 'flag_review', label: 'Flag Review' },
  { id: 'file_prep', label: 'File Prep' },
  { id: 'manual', label: 'Manual' },
] as const;

const TYPE_OPTIONS: ReadonlyArray<{ value: TaskType; label: string }> = [
  { value: 'po_approval_needed', label: 'PO Approval' },
  { value: 'receiving_ready', label: 'Receiving' },
  { value: 'discrepancy_review', label: 'Discrepancy Review' },
  { value: 'flag_review', label: 'Flag Review' },
  { value: 'file_prep', label: 'File Prep' },
  { value: 'manual', label: 'Manual' },
] as const;

const PRIORITY_OPTIONS: ReadonlyArray<{ value: TaskPriority; label: string }> = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

const TYPE_LABELS: Record<string, string> = {
  po_approval_needed: 'PO Approval',
  receiving_ready: 'Receiving',
  discrepancy_review: 'Discrepancy',
  flag_review: 'Flag Review',
  file_prep: 'File Prep',
  manual: 'Manual',
};

const ACTION_CONFIG: Record<string, ActionConfigEntry> = {
  po_approval_needed: { label: 'Review PO', fallback: '/purchasing/purchase-orders' },
  receiving_ready: { label: 'Go to Receiving', fallback: '/purchasing/receiving' },
  discrepancy_review: { label: 'View Issues', fallback: '/purchasing/discrepancies' },
  flag_review: { label: 'Review Flags', fallback: '/catalog/flagged' },
  file_prep: { label: 'Go to Files', fallback: '/files' },
  manual: { label: 'View', fallback: null },
};

// ── Badge components ──────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: string }): JSX.Element {
  const cls: string =
    (
      {
        critical: 'bg-danger/15 text-danger border border-danger/30',
        high: 'bg-warning/15 text-warning border border-warning/30',
        medium: 'bg-info/15 text-info border border-info/30',
        low: 'bg-surface-3 text-ink-muted border border-border',
      } as Record<string, string>
    )[priority] || 'bg-surface-3 text-ink-muted';
  const label: string =
    (
      { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' } as Record<string, string>
    )[priority] || priority;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const cls: string =
    (
      {
        open: 'bg-info/15 text-info',
        in_progress: 'bg-warning/15 text-warning',
        completed: 'bg-success/15 text-success',
        cancelled: 'bg-surface-3 text-ink-muted',
        overdue: 'bg-danger/15 text-danger',
      } as Record<string, string>
    )[status] || 'bg-surface-3 text-ink-muted';
  const label: string =
    (
      {
        open: 'Open',
        in_progress: 'In Progress',
        completed: 'Completed',
        cancelled: 'Cancelled',
      } as Record<string, string>
    )[status] || status;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

function TypeBadge({ taskType }: { taskType: string }): JSX.Element {
  const label = TYPE_LABELS[taskType] || taskType;
  const cls =
    taskType === 'manual' ? 'bg-surface-3 text-ink-muted' : 'bg-surface-3 text-ink-secondary';
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

// ── Date formatting ───────────────────────────────────────────────────────────

function formatDueDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d.getTime() - now.getTime()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `in ${diffDays}d`;
  return d.toLocaleDateString();
}

function isDueOverdue(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return d < now;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Filter pill component ─────────────────────────────────────────────────────

interface FilterGroupProps<T extends string | null> {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}

function FilterGroup<T extends string | null>({
  options,
  value,
  onChange,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
      {options.map((f) => (
        <button
          key={f.id ?? 'all'}
          onClick={() => onChange(f.id)}
          aria-pressed={value === f.id}
          className={clsx(
            'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
            value === f.id
              ? 'bg-surface-4 text-ink-bright'
              : 'text-ink-muted hover:text-ink-secondary'
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Create Task Modal ─────────────────────────────────────────────────────────

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface CreateTaskErrors {
  title?: string;
  submit?: string;
}

function CreateTaskModal({ open, onClose, onCreated }: CreateTaskModalProps): JSX.Element | null {
  const [title, setTitle] = useState<string>('');
  const [taskType, setTaskType] = useState<TaskType>('manual');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [description, setDescription] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [deepLink, setDeepLink] = useState<string>('');
  const [errors, setErrors] = useState<CreateTaskErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  function reset(): void {
    setTitle('');
    setTaskType('manual');
    setPriority('medium');
    setDescription('');
    setDueDate('');
    setDeepLink('');
    setErrors({});
    setSaving(false);
  }

  const handleClose = useCallback((): void => {
    reset();
    onClose();
  }, [onClose]);

  async function handleSubmit(): Promise<void> {
    const errs: CreateTaskErrors = {};
    if (!title.trim()) errs.title = 'Title is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    // Day 84.6 P4.5.a — create_task envelope-extract per Decision 45 + Path B
    // audit 🔴 silent-broken fix. Pre-Phase-1 returned SETOF tasks (array of
    // task rows); post-migration returns { ok, data: <task row>, userErrors }
    // jsonb envelope (single object, not array). The `(data ?? []) as Array<...>`
    // cast was wrapping the envelope object as a 1-element array; downstream
    // `t.assigned_to` read undefined.
    const createResult = await callRpc('create_task', {
      p_title: title.trim(),
      p_task_type: taskType,
      p_priority: priority,
      p_description: description.trim() || null,
      p_due_date: dueDate || null,
      p_deep_link_path: deepLink.trim() || null,
    });
    setSaving(false);
    const createEnv = extractEnvelope<TaskNotifyShape>(createResult);
    if (!createEnv.isOk) {
      setErrors({ submit: createEnv.userErrors[0]?.message ?? 'Create failed' });
      return;
    }
    // task_assigned fires when a newly-created task lands with a non-null
    // assignee. The Create Task modal does not currently expose an Assignee
    // picker so this branch is dormant on Day 49 — wired now so the assign
    // UI added in a future Tasks polish day fires correctly without re-touching
    // this site.
    const created: TaskNotifyShape[] = createEnv.data ? [createEnv.data] : [];
    for (const t of created) {
      if (t.assigned_to) {
        createNotification({
          userId: t.assigned_to,
          event: 'task_assigned',
          title: `Task assigned: ${t.title ?? 'Untitled'}`,
          message: 'A new task has been assigned to you.',
          link: `/tasks?id=${t.id}`,
        });
      }
    }
    reset();
    triggerTicker('Task created');
    onCreated();
    onClose();
  }

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={handleClose}
        className="absolute inset-0 bg-black/50 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="New Task"
        className="relative bg-surface-1 border border-border rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink-bright">New Task</h2>
          <button
            onClick={handleClose}
            className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="task-title"
              className="block text-xs font-medium text-ink-secondary mb-1"
            >
              Title <span className="text-danger">*</span>
            </label>
            <input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="arbi-input w-full text-sm"
              placeholder="What needs to be done?"
            />
            {errors.title && <p className="text-xs text-danger mt-1">{errors.title}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="task-type"
                className="block text-xs font-medium text-ink-secondary mb-1"
              >
                Task Type <span className="text-danger">*</span>
              </label>
              <div id="task-type">
                <ArbiSelect
                  value={taskType}
                  options={[...TYPE_OPTIONS]}
                  onChange={(v) => setTaskType(String(v) as TaskType)}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="task-priority"
                className="block text-xs font-medium text-ink-secondary mb-1"
              >
                Priority <span className="text-danger">*</span>
              </label>
              <div id="task-priority">
                <ArbiSelect
                  value={priority}
                  options={[...PRIORITY_OPTIONS]}
                  onChange={(v) => setPriority(String(v) as TaskPriority)}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="task-description"
              className="block text-xs font-medium text-ink-secondary mb-1"
            >
              Description
            </label>
            <textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="arbi-input w-full text-xs resize-none"
              placeholder="Optional details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="task-due-date"
                className="block text-xs font-medium text-ink-secondary mb-1"
              >
                Due Date
              </label>
              <input
                id="task-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="arbi-input w-full text-xs"
              />
            </div>
            <div>
              <label
                htmlFor="task-deep-link"
                className="block text-xs font-medium text-ink-secondary mb-1"
              >
                Deep Link
              </label>
              <input
                id="task-deep-link"
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                className="arbi-input w-full text-xs"
                placeholder="/purchasing/receiving"
              />
            </div>
          </div>
        </div>

        {errors.submit && <p className="text-xs text-danger">{errors.submit}</p>}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border-subtle">
          <button onClick={handleClose} className="arbi-btn-ghost text-xs px-3 py-1.5">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="arbi-btn-primary text-xs px-3 py-1.5"
          >
            {saving ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────────────

interface TaskDetailPanelProps {
  task: TaskRow | null;
  onClose: () => void;
  onAction: (taskId: string, status: TaskStatus) => void;
  navigate: NavigateFunction;
  namesById: Record<string, string>;
}

function TaskDetailPanel({
  task,
  onClose,
  onAction,
  navigate,
  namesById,
}: TaskDetailPanelProps): JSX.Element | null {
  if (!task) return null;

  const actionCfg = ACTION_CONFIG[task.task_type];
  const showActions = task.status !== 'completed' && task.status !== 'cancelled';
  const actionPath = task.deep_link_path || actionCfg?.fallback;

  return (
    <div className="w-80 flex-shrink-0 bg-surface-1 border-l border-border h-full overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-ink-bright truncate pr-2">{task.title}</h3>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <PriorityBadge priority={task.priority} />
          <StatusBadge status={task.status} />
        </div>
        <TypeBadge taskType={task.task_type} />

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Description</p>
          <p className="text-xs text-ink-primary">
            {task.description || <span className="text-ink-muted">No description</span>}
          </p>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Assignee</p>
          <p className="text-xs text-ink-secondary">
            {task.assigned_to ? (
              resolveAssigneeLabel(task.assigned_to, namesById)
            ) : (
              <span className="text-ink-muted">Unassigned</span>
            )}
          </p>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Due Date</p>
          <p
            className={clsx(
              'text-xs font-mono',
              isDueOverdue(task.due_date) ? 'text-danger' : 'text-ink-secondary'
            )}
          >
            {task.due_date ? (
              formatDueDate(task.due_date)
            ) : (
              <span className="text-ink-muted">—</span>
            )}
          </p>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Created</p>
          <p className="text-xs font-mono text-ink-muted">{relativeTime(task.created_at)}</p>
        </div>

        {actionPath && showActions && (
          <button
            onClick={() => navigate(actionPath)}
            className="arbi-btn-ghost text-xs w-full justify-center"
          >
            {actionCfg?.label || 'View'} <ExternalLink size={12} />
          </button>
        )}

        {showActions && (
          <>
            <div className="border-t border-border-subtle pt-3 space-y-2">
              <button
                onClick={() => onAction(task.id, 'completed')}
                className="arbi-btn-primary text-xs w-full justify-center"
              >
                <CheckCircle size={12} /> Mark Complete
              </button>
              <button
                onClick={() => onAction(task.id, 'cancelled')}
                className="arbi-btn-ghost text-xs w-full justify-center text-danger"
              >
                Cancel Task
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Tasks Page ───────────────────────────────────────────────────────────

export default function Tasks(): JSX.Element {
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer + dist_rep are both read-only on Tasks. Folds in
  // B14 follow-up (BACKLOG line 601 "UI polish to hide bulk actions for
  // dist_rep on Tasks.tsx — separate follow-up") alongside the viewer gate.
  // Both roles see grid + filters + export + per-row View action (navigation,
  // not mutation), but the bulk action bar, Create button, and row-selection
  // checkboxes are hidden so they never click into an RLS-denied write path.
  const { isViewer, isDistRep } = useApp();
  const isReadOnly = isViewer || isDistRep;
  const gridRef = useRef<AgGridReact<TaskRow> | null>(null);
  const gridApiRef = useRef<GridApi<TaskRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | null>(null);
  const [typeFilter, setTypeFilter] = useState<TaskType | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<TaskRow[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [detailTask, setDetailTask] = useState<TaskRow | null>(null);
  // D-F9 — profiles id→full_name map for the Assignee column + detail panel. RLS
  // filters this (admin → all profiles; other roles → their own), so a foreign
  // assignee on another role's task falls back to a short id (resolveAssigneeLabel).
  const [assigneeNames, setAssigneeNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let active = true;
    void supabase
      .from('profiles')
      .select('id, full_name')
      .then(({ data }) => {
        if (!active || !data) return;
        const map: Record<string, string> = {};
        for (const p of data as Array<{ id: string; full_name: string | null }>) {
          if (p.full_name) map[p.id] = p.full_name;
        }
        setAssigneeNames(map);
      });
    return () => {
      active = false;
    };
  }, []);
  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);

  const showToast = useCallback((t: ToastState): void => setToast(t), []);
  const hasFilters = Boolean(statusFilter || priorityFilter || typeFilter || searchText.trim());

  // State ref for getRows
  const stateRef = useRef<{
    searchText: string;
    statusFilter: TaskStatus | null;
    priorityFilter: TaskPriority | null;
    typeFilter: TaskType | null;
  }>({ searchText, statusFilter, priorityFilter, typeFilter });
  stateRef.current = { searchText, statusFilter, priorityFilter, typeFilter };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const {
      searchText: st,
      statusFilter: sf,
      priorityFilter: pf,
      typeFilter: tf,
    } = stateRef.current;

    try {
      const { data, error } = await callRpc('get_tasks', {
        p_status: sf || null,
        p_priority: pf || null,
        p_task_type: tf || null,
        p_search: st.trim() || null,
        p_limit: endRow - startRow,
        p_offset: startRow,
      });
      if (error) throw new Error(error.message);

      const rows = (data as unknown as TaskRow[]) || [];
      const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
      setTotalCount(total);
      successCallback(rows, total);
    } catch (e) {
      reportDatasourceFailure(
        'Tasks',
        { rpcName: 'get_tasks', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<TaskRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  // Refresh grid when filters change
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, priorityFilter, typeFilter, getRowsFn]);

  function refreshGrid(): void {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }

  // Selection tracking
  const onSelectionChanged = useCallback((): void => {
    if (!gridApiRef.current) return;
    setSelectedRows(gridApiRef.current.getSelectedRows());
  }, []);

  // Bulk actions
  async function bulkUpdateStatus(status: TaskStatus): Promise<void> {
    const ids = selectedRows.map((r) => r.id);
    // Pre-snapshot of current assignees keyed by task id — needed to detect
    // NULL→user / user_a→user_b transitions against the RPC's post-state. The
    // existing bulk action path is status-only (no p_assigned_to passed), so
    // no transitions can happen today; the wiring is preparatory for a future
    // bulk-reassign UI (Path A migration extended bulk_update_tasks RETURNS to
    // SETOF tasks specifically to enable this comparison).
    const preAssignees = new Map(selectedRows.map((r) => [r.id, r.assigned_to]));
    // Day 84.6 P4.5.b — bulk_update_tasks per-row envelope-extract. Pre-Phase-1
    // returned SETOF tasks; post-migration returns TABLE(task_id, ok, data,
    // user_errors). The flat-cast `(data ?? []) as Array<...>` reading row-shape
    // fields was broken (data is BulkRpcRow not Task).
    const bulkResult = await callRpc('bulk_update_tasks', {
      p_task_ids: ids,
      p_status: status,
    });
    const result = extractBulkEnvelope<TaskNotifyShape, 'task_id'>(bulkResult);
    if (!result.isOk && result.rows.length === 0) {
      showToast({ kind: 'error', message: bulkResult.error?.message ?? 'Bulk update failed' });
      return;
    }
    const successRows = result.rows.filter((r) => r.ok);
    const successTasks: TaskNotifyShape[] = successRows
      .map((r) => r.data)
      .filter((t): t is TaskNotifyShape => t !== null);
    fireAssigneeTransitions(successTasks, preAssignees);
    const successCount = successRows.length;
    const failedCount = result.rows.length - successCount;
    if (result.hasAnyFailure) {
      console.warn(
        '[Tasks] bulk-update partial-failure:',
        result.rows
          .filter((r) => !r.ok)
          .map((r) => ({
            task_id: r.task_id,
            code: r.user_errors[0]?.code,
            message: r.user_errors[0]?.message,
          }))
      );
      showToast({
        kind: 'error',
        message: `${successCount} of ${result.rows.length} tasks updated; ${failedCount} failed`,
      });
    } else {
      showToast({
        kind: 'success',
        message: `${successCount} task${successCount !== 1 ? 's' : ''} updated`,
      });
    }
    setSelectedRows([]);
    refreshGrid();
  }

  // Single task status update (from detail panel)
  async function updateTaskStatus(taskId: string, status: TaskStatus): Promise<void> {
    const preAssignee = detailTask?.assigned_to ?? null;
    // Day 84.6 P4.5.c — update_task_status envelope-extract. Pre-Phase-1 returned
    // SETOF tasks; post-migration returns { ok, data: <task row>, userErrors }
    // single-object envelope. fireAssigneeTransitions wraps in array.
    const updateResult = await callRpc('update_task_status', {
      p_task_id: taskId,
      p_status: status,
    });
    const updateEnv = extractEnvelope<TaskNotifyShape>(updateResult);
    if (!updateEnv.isOk) {
      showToast({ kind: 'error', message: updateEnv.userErrors[0]?.message ?? 'Update failed' });
      return;
    }
    const updatedTasks: TaskNotifyShape[] = updateEnv.data ? [updateEnv.data] : [];
    fireAssigneeTransitions(updatedTasks, new Map([[taskId, preAssignee]]));
    showToast({ kind: 'success', message: 'Task updated' });
    setDetailTask(null);
    refreshGrid();
  }

  // ── Cell renderers ──────────────────────────────────────────

  const PriorityCell = useCallback((p: ICellRendererParams<TaskRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <PriorityBadge priority={p.data.priority} />;
  }, []);

  const StatusCell = useCallback((p: ICellRendererParams<TaskRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <StatusBadge status={p.data.status} />;
  }, []);

  const TypeCell = useCallback((p: ICellRendererParams<TaskRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <TypeBadge taskType={p.data.task_type} />;
  }, []);

  const DueDateCell = useCallback((p: ICellRendererParams<TaskRow>): JSX.Element | null => {
    if (!p.data) return null;
    const overdue = isDueOverdue(p.data.due_date);
    return (
      <span className={clsx('font-mono text-xs', overdue ? 'text-danger' : 'text-ink-muted')}>
        {formatDueDate(p.data.due_date)}
      </span>
    );
  }, []);

  const AssigneeCell = useCallback(
    (p: ICellRendererParams<TaskRow>): JSX.Element | null => {
      if (!p.data) return null;
      const label = resolveAssigneeLabel(p.data.assigned_to, assigneeNames);
      return (
        <span
          className="truncate text-xs text-ink-secondary"
          title={p.data.assigned_to ? label : undefined}
        >
          {label}
        </span>
      );
    },
    [assigneeNames]
  );

  const ActionCell = useCallback(
    (p: ICellRendererParams<TaskRow>): JSX.Element | null => {
      if (!p.data || p.data.status === 'completed' || p.data.status === 'cancelled') return null;
      const cfg = ACTION_CONFIG[p.data.task_type];
      const path = p.data.deep_link_path || cfg?.fallback;
      if (!path) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(path);
          }}
          className="arbi-btn-ghost text-2xs py-0.5 px-2"
        >
          {cfg?.label || 'View'} <ExternalLink size={12} />
        </button>
      );
    },
    [navigate]
  );

  // ── Column definitions ──────────────────────────────────────

  const columnDefs = useMemo<ColDef<TaskRow>[]>(
    () =>
      [
        {
          colId: 'task_id',
          headerName: 'ID',
          field: 'id',
          suppressMovable: true,
          resizable: false,
          width: 80,
          minWidth: 80,
          pinned: 'left',
          sortable: true,
          cellStyle: { display: 'flex', alignItems: 'center' },
          valueFormatter: (p: ValueFormatterParams<TaskRow>) =>
            p.value ? '#' + String(p.value).slice(-4) : '\u2014',
          cellClass: 'font-mono text-xs text-ink-muted',
        },
        {
          headerName: 'Priority',
          field: 'priority',
          width: 100,
          sortable: false,
          cellRenderer: PriorityCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          sortable: false,
          cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-primary)' },
          tooltipField: 'title',
        },
        {
          headerName: 'Type',
          field: 'task_type',
          width: 120,
          sortable: false,
          cellRenderer: TypeCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Assignee',
          field: 'assigned_to',
          width: 100,
          sortable: false,
          cellRenderer: AssigneeCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Due Date',
          field: 'due_date',
          width: 110,
          sortable: false,
          cellRenderer: DueDateCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          sortable: false,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Action',
          width: 110,
          sortable: false,
          filter: false,
          cellRenderer: ActionCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
      ] as ColDef<TaskRow>[],
    [PriorityCell, StatusCell, TypeCell, DueDateCell, AssigneeCell, ActionCell]
  );

  const defaultColDef = useMemo<ColDef<TaskRow>>(
    () => ({
      sortable: false,
      resizable: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerFontSize: '10px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<TaskRow>): void => {
    if (!event.data) return;
    // Don't open detail if clicking checkbox or action button
    const domEvent = event.event as Event | undefined;
    const target = domEvent?.target as HTMLElement | null;
    if (target?.closest('button') || target?.closest('.ag-checkbox-input')) return;
    setDetailTask(event.data);
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <CreateTaskModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          showToast({ kind: 'success', message: 'Task created' });
          refreshGrid();
        }}
      />
      <ConfirmDialog
        open={confirmCancel}
        title="Cancel selected tasks?"
        message={`This will cancel ${selectedRows.length} task(s). This action cannot be undone.`}
        onConfirm={() => {
          bulkUpdateStatus('cancelled');
          setConfirmCancel(false);
        }}
        onCancel={() => setConfirmCancel(false)}
      />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Tasks</h1>
            <span className="text-xs text-ink-muted font-mono">
              {totalCount === 1 ? '1 task' : `${totalCount.toLocaleString()} tasks`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-tasks" />
            {!isReadOnly && (
              <button
                onClick={() => setCreateOpen(true)}
                className="arbi-btn-primary text-xs px-3 py-1.5"
              >
                <Plus size={16} /> New Task
              </button>
            )}
          </div>
        </div>

        {/* Row 2: filters or bulk actions */}
        {selectedRows.length > 0 && !isReadOnly ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-ink-secondary font-medium">
              {selectedRows.length} task(s) selected
            </span>
            <button
              onClick={() => bulkUpdateStatus('completed')}
              className="arbi-btn-ghost text-xs px-2 py-1"
            >
              <CheckCircle size={12} /> Mark Complete
            </button>
            <button
              onClick={() => setConfirmCancel(true)}
              className="arbi-btn-ghost text-xs px-2 py-1 text-danger"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <FilterGroup options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
            <FilterGroup
              options={PRIORITY_FILTERS}
              value={priorityFilter}
              onChange={setPriorityFilter}
            />
            <FilterGroup options={TYPE_FILTERS} value={typeFilter} onChange={setTypeFilter} />
            <div className="relative ml-auto">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
              />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search tasks..."
                aria-label="Search tasks"
                className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
              />
            </div>
          </div>
        )}
      </div>

      {/* Grid + optional detail panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={50}
            selectionColumnDef={SELECTION_COLUMN_DEF}
            rowSelection={{
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: !isReadOnly,
              headerCheckbox: false,
            }}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            onSelectionChanged={onSelectionChanged}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            overlayNoRowsTemplate={
              hasFilters
                ? '<span style="color:var(--ink-muted);font-size:13px">No tasks match your filters</span>'
                : '<span style="color:var(--ink-muted);font-size:13px">No tasks yet — tasks are created automatically or you can create them manually</span>'
            }
          />
        </div>

        {/* Detail panel */}
        {detailTask && (
          <TaskDetailPanel
            task={detailTask}
            onClose={() => setDetailTask(null)}
            onAction={updateTaskStatus}
            navigate={navigate}
            namesById={assigneeNames}
          />
        )}
      </div>
    </div>
  );
}
